import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
// Define types for our component
interface JsonToMarkdownProps {}

// Markdown theme options
type MarkdownTheme = 'standard' | 'github' | 'detailed' | 'minimal';

// Markdown content display options
type DisplayOption = 'expanded' | 'collapsed';

const JsonToMarkdown: React.FC<JsonToMarkdownProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Markdown output
  const [markdownOutput, setMarkdownOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for Markdown theme
  const [theme, setTheme] = useState<MarkdownTheme>('standard');
  // State for display option
  const [displayOption, setDisplayOption] = useState<DisplayOption>('expanded');
  // State to track if the conversion happened
  const [isMarkdownGenerated, setIsMarkdownGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for Markdown preview
  const [showPreview, setShowPreview] = useState<boolean>(true);


  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsMarkdownGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsMarkdownGenerated(false);
    setMarkdownOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setMarkdownOutput('');
    setError('');
    setFileName('json_data');
    setTheme('standard');
    setDisplayOption('expanded');
    setIsMarkdownGenerated(false);
  };

  // Generate Markdown representation of JSON object
  const generateJsonMarkdown = (obj: any, indent: number = 0, isArrayItem: boolean = false, path: string[] = []): string => {
    const spaces = ' '.repeat(indent * 2);
    let md = '';

    if (obj === null) {
      return 'null';
    }

    if (typeof obj === 'undefined') {
      return 'undefined';
    }

    if (typeof obj === 'string') {
      return `"${obj.replace(/"/g, '\\"')}"`;
    }

    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return String(obj);
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return '[]';
      }

      // For detailed theme, we use blockquotes to indicate nesting
      const isDetailedTheme = theme === 'detailed';
      
      // For top-level arrays, we add a heading with the path
      if (path.length > 0 && path[path.length - 1] && indent === 0 && isDetailedTheme) {
        md += `## ${path[path.length - 1]}\n\n`;
      }

      obj.forEach((item, index) => {
        if (displayOption === 'collapsed' && typeof item === 'object' && item !== null) {
          if (isArrayItem) {
            md += `${spaces}- \`[complex item]\`\n`;
          } else {
            md += `${spaces}* \`[${index}]: [complex item]\`\n`;
          }
        } else {
          const itemPath = [...path, index.toString()];
          
          if (typeof item === 'object' && item !== null) {
            if (isDetailedTheme) {
              md += `${spaces}- **Item ${index}:**\n`;
              md += generateJsonMarkdown(item, indent + 1, false, itemPath);
            } else {
              md += `${spaces}- `;
              md += generateJsonMarkdown(item, indent + 1, true, itemPath);
            }
          } else {
            if (isArrayItem) {
              md += `${spaces}- ${generateJsonMarkdown(item, 0, false, itemPath)}\n`;
            } else {
              md += `${spaces}* \`[${index}]:\` ${generateJsonMarkdown(item, 0, false, itemPath)}\n`;
            }
          }
        }
      });

      return md;
    }

    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      
      if (keys.length === 0) {
        return '{}';
      }

      const isDetailedTheme = theme === 'detailed';
      const isGithubTheme = theme === 'github';
      
      // For top-level objects, we add a heading with the path
      if (path.length > 0 && path[path.length - 1] && indent === 0 && isDetailedTheme) {
        md += `## ${path[path.length - 1]}\n\n`;
      }

      // For GitHub theme, we use tables for top-level objects
      if (isGithubTheme && indent === 0 && !isArrayItem) {
        md += '| Key | Value |\n|-----|-------|\n';
        
        keys.forEach(key => {
          const value = obj[key];
          const keyPath = [...path, key];
          
          if (typeof value === 'object' && value !== null) {
            if (displayOption === 'collapsed') {
              md += `| \`${key}\` | \`[complex object]\` |\n`;
            } else {
              // For complex objects in GitHub theme, use sub-sections
              md += `| \`${key}\` | *see below* |\n`;
            }
          } else {
            md += `| \`${key}\` | ${generateJsonMarkdown(value, 0, false, keyPath).replace(/\n/g, '<br>')} |\n`;
          }
        });

        // Add subsections for complex objects if not collapsed
        if (displayOption !== 'collapsed') {
          md += '\n';
          keys.forEach(key => {
            const value = obj[key];
            const keyPath = [...path, key];
            
            if (typeof value === 'object' && value !== null) {
              md += `### ${key}\n\n`;
              md += generateJsonMarkdown(value, 0, false, keyPath);
              md += '\n';
            }
          });
        }
      } else {
        // For other themes or nested objects
        keys.forEach(key => {
          const value = obj[key];
          const keyPath = [...path, key];
          
          if (displayOption === 'collapsed' && typeof value === 'object' && value !== null) {
            if (isArrayItem) {
              md += `${spaces}${key}: \`[complex object]\`\n`;
            } else {
              md += `${spaces}* **${key}**: \`[complex object]\`\n`;
            }
          } else {
            if (typeof value === 'object' && value !== null) {
              if (isDetailedTheme) {
                if (isArrayItem) {
                  md += `${spaces}**${key}**:\n`;
                } else {
                  md += `${spaces}* **${key}**:\n`;
                }
                md += generateJsonMarkdown(value, indent + 1, false, keyPath);
              } else if (isArrayItem) {
                md += `${spaces}**${key}**: ${generateJsonMarkdown(value, indent + 1, false, keyPath)}`;
              } else {
                md += `${spaces}* **${key}**:`;
                if (Array.isArray(value)) {
                  md += '\n';
                }
                md += generateJsonMarkdown(value, indent + 1, false, keyPath);
              }
            } else {
              if (isArrayItem) {
                md += `${spaces}**${key}**: ${generateJsonMarkdown(value, 0, false, keyPath)}`;
                if (keys.indexOf(key) < keys.length - 1) {
                  md += ' | ';
                } else {
                  md += '\n';
                }
              } else {
                md += `${spaces}* **${key}**: ${generateJsonMarkdown(value, 0, false, keyPath)}\n`;
              }
            }
          }
        });
      }

      return md;
    }

    return String(obj);
  };

  // Convert JSON to Markdown
  const convertJsonToMarkdown = () => {
    setError('');
    setIsLoading(true);
    setIsMarkdownGenerated(false);
    setMarkdownOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      let markdown = '';
      
      // Add title based on file name if in detailed or GitHub theme
      if (theme === 'detailed' || theme === 'github') {
        markdown += `# ${fileName || 'JSON Data'}\n\n`;
      }
      
      // Add description or summary based on theme
      if (theme === 'detailed') {
        markdown += `*Generated markdown representation of JSON data*\n\n`;
        
        // Add TOC for detailed theme
        const topLevelKeys = Object.keys(jsonData);
        if (topLevelKeys.length > 3 && typeof jsonData === 'object' && !Array.isArray(jsonData)) {
          markdown += `## Table of Contents\n\n`;
          topLevelKeys.forEach(key => {
            markdown += `* [${key}](#${key.toLowerCase().replace(/\s+/g, '-')})\n`;
          });
          markdown += `\n`;
        }
      }
      
      // Generate the actual markdown content
      markdown += generateJsonMarkdown(jsonData, 0, false, ['root']);
      
      // Add footer for detailed theme
      if (theme === 'detailed') {
        markdown += `\n\n---\n\n*Generated on ${new Date().toLocaleString()}*`;
      }
      
      setMarkdownOutput(markdown);
      setIsMarkdownGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Markdown: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Markdown to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(markdownOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Markdown file
  const downloadMarkdown = () => {
    const blob = new Blob([markdownOutput], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'json_data'}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Simple Markdown parser for preview
  // Note: This is a basic implementation and doesn't handle all Markdown syntax
  const parseMarkdown = (markdown: string): string => {
    let html = markdown
      // Headers
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Code
      .replace(/`(.*?)`/g, '<code>$1</code>')
      // Lists
      .replace(/^\* (.*$)/gm, '<li>$1</li>')
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      // Tables
      .replace(/\|(.+)\|/g, '<tr><td>$1</td></tr>')
      .replace(/<tr><td>-+<\/td><\/tr>/g, '')
      // Line breaks
      .replace(/\n/g, '<br>');
    
    // Wrap lists
    html = html.replace(/<li>(.+)<\/li><br><li>/g, '<li>$1</li><li>');
    html = html.replace(/<li>(.+)<\/li><br>/g, '<ul><li>$1</li></ul>');
    
    // Wrap tables
    html = html.replace(/<tr>(.+)<\/tr><br><tr>/g, '<tr>$1</tr><tr>');
    html = html.replace(/<tr>(.+)<\/tr><br>/g, '<table><tr>$1</tr></table>');
    
    return html;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Markdown Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to formatted Markdown documents. Paste your JSON data below to generate a readable Markdown representation.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                    Markdown Style
                  </label>
                  <select
                    id="theme"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value as MarkdownTheme)}
                  >
                    <option value="standard">Standard (Bullet points)</option>
                    <option value="github">GitHub (Tables)</option>
                    <option value="detailed">Detailed (Headings & TOC)</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="displayOption" className="block text-sm font-medium text-gray-700 mb-1">
                    Display Option
                  </label>
                  <select
                    id="displayOption"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={displayOption}
                    onChange={(e) => setDisplayOption(e.target.value as DisplayOption)}
                  >
                    <option value="expanded">Expanded (show all nested content)</option>
                    <option value="collapsed">Collapsed (simplify complex objects)</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPreview"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                    Show Preview
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToMarkdown}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Markdown'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Markdown Output
            </label>
            
            {isMarkdownGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Markdown'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadMarkdown}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .md
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isMarkdownGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Markdown output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isMarkdownGenerated && (
              <div className="flex-grow flex flex-col">
                {showPreview ? (
                  <div className="mb-3">
                    <div className="mb-2 flex justify-between items-center">
                      <h3 className="font-medium text-gray-800">Preview:</h3>
                      <button
                        className="text-xs text-gray-600 hover:text-gray-800"
                        onClick={() => setShowPreview(false)}
                      >
                        Hide Preview
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded-md h-48 overflow-auto p-3 mb-3">
                      <div 
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(markdownOutput) }}
                        className="markdown-preview"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => setShowPreview(true)}
                    >
                      Show Preview
                    </button>
                  </div>
                )}
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="markdownOutput" className="text-sm font-medium text-gray-700 mb-1">
                    Markdown Code:
                  </label>
                  <textarea
                    id="markdownOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={markdownOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isMarkdownGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Markdown Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated Markdown can be used in GitHub, documentation sites, or any Markdown editor.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Markdown Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into formatted Markdown documents. The converter preserves your JSON structure
              while creating a readable and shareable Markdown representation.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple Markdown Styles</strong> - Choose from standard, GitHub, detailed, or minimal styles</li>
              <li><strong>Display Options</strong> - Expanded view shows all nested content, collapsed simplifies complex objects</li>
              <li><strong>Live Preview</strong> - See how the Markdown will look before downloading</li>
              <li><strong>Export Options</strong> - Copy to clipboard or download as .md file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>For large JSON structures with many nested objects, try the "Detailed" style with headings</li>
              <li>For simple tabular data, the "GitHub" style creates nice-looking tables</li>
              <li>The "Standard" style works well for most JSON structures and is the most readable</li>
              <li>For deeply nested structures, use the "Collapsed" display option to simplify the output</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Markdown Usage</h3>
            <p className="text-gray-600">
              The generated Markdown can be used in:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>GitHub repositories (README files, wikis, issues)</li>
              <li>Documentation websites (GitBook, ReadTheDocs, etc.)</li>
              <li>Markdown editors (VS Code, Typora, etc.)</li>
              <li>Content management systems that support Markdown</li>
              <li>Team communication tools (Slack, Discord, etc.)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToMarkdown;